/**
 * LiveMonitor.tsx — the THREE-COLUMN live "watch the call" test view.
 *
 * A console-style monitor for watching a live call as it happens. Three equal,
 * independently-scrolling columns, newest-anchored:
 *
 *   1. CUSTOMER  — the customer's live transcript (final lines committed; at most
 *                  one lighter/italic INTERIM line pinned at the bottom).
 *   2. REP       — the rep's live transcript (same interim behavior).
 *   3. AI COACH  — the live rubric checklist (4 criteria, green check + running
 *                  points /100) on top, then severity-colored NUDGE cards
 *                  (newest first). Active nudges auto-remove after their ttlMs;
 *                  a small history list below keeps nothing lost during a test.
 *
 * TRANSPORT
 *   A WebSocket to `${WS_BASE}/coach?monitor=1&token=<Supabase JWT>`, where
 *   WS_BASE comes from VITE_VOICE_WS_URL (default ws://localhost:8788). The
 *   signed-in user's Supabase access token AUTHENTICATES the socket: the voice
 *   service verifies it against the project JWKS and requires an active,
 *   same-tenant profile with the admin-managed can_view_live_monitor
 *   permission, plus its VOICE_ALLOW_TEST_MONITOR master switch. There is NO
 *   unauthenticated mode (the old no-auth ?test=1 path was removed). The socket
 *   auto-reconnects with backoff if it drops; a status pill shows the current
 *   connection state.
 *
 * ROUTING: /live-monitor renders via RequireLiveMonitor (main.tsx) — signed-in
 * users with can_view_live_monitor only. That gate is UX; the WebSocket
 * re-checks the same permission server-side on connect and re-validates it
 * periodically while connected.
 */
import { useEffect, useReducer, useRef, useState, type ReactNode } from "react";
import { Badge } from "@/components/ui";
import { getAccessToken } from "@/lib/supabase";
import {
  RUBRIC,
  type CoachingEvent,
  type CriterionState,
  type Nudge,
  type RubricState,
  type TranscriptLine,
} from "@/types";

/** Debug-feed item types (raw RingCentral visualizer). */
type RcEvent = Extract<CoachingEvent, { type: "rc_event" }>;
type RcStatus = Extract<CoachingEvent, { type: "rc_status" }>;
/** Cap the raw event feed so it can't grow unbounded during a long session. */
const RC_FEED_MAX = 60;

/* -------------------------------------------------------------------------- */
/* Config                                                                       */
/* -------------------------------------------------------------------------- */

const WS_BASE =
  (import.meta.env.VITE_VOICE_WS_URL as string | undefined) ?? "ws://localhost:8788";

/** Reconnect backoff schedule (ms). Caps at the last value. */
const BACKOFF_MS = [1000, 2000, 4000, 8000, 15000];

/* -------------------------------------------------------------------------- */
/* State                                                                        */
/* -------------------------------------------------------------------------- */

type CallPhase = "idle" | "live" | "ended";
type ConnStatus = "connecting" | "open" | "closed";

interface MonitorState {
  phase: CallPhase;
  counterpart: string | null;
  startedAt: string | null;
  endedAt: string | null;
  /** Committed (final) lines, in arrival order. */
  customerLines: TranscriptLine[];
  repLines: TranscriptLine[];
  /** At most one pending interim line per speaker (replaced as new ones arrive). */
  customerInterim: TranscriptLine | null;
  repInterim: TranscriptLine | null;
  /** 'unknown'-speaker final lines land here (muted, shared spot). */
  unknownLines: TranscriptLine[];
  rubric: RubricState;
  /** Currently-visible nudges, newest first. */
  nudges: Nudge[];
  /** Every nudge ever seen (newest first) — the test history, never auto-pruned. */
  nudgeHistory: Nudge[];
  /** Raw RingCentral telephony events (newest first) — the debug visualizer. */
  rcEvents: RcEvent[];
  /** Latest RingCentral socket status (connected/subscribed/closed/…). */
  rcStatus: RcStatus | null;
}

/** The rubric at rest: nothing covered yet. */
function freshRubric(): RubricState {
  return RUBRIC.map((c) => ({ ...c, covered: false, coveredAtMs: null }));
}

function initialState(): MonitorState {
  return {
    phase: "idle",
    counterpart: null,
    startedAt: null,
    endedAt: null,
    customerLines: [],
    repLines: [],
    customerInterim: null,
    repInterim: null,
    unknownLines: [],
    rubric: freshRubric(),
    nudges: [],
    nudgeHistory: [],
    rcEvents: [],
    rcStatus: null,
  };
}

type Action =
  | { kind: "event"; event: CoachingEvent }
  | { kind: "expireNudge"; id: string }
  | { kind: "reset" };

function reducer(state: MonitorState, action: Action): MonitorState {
  switch (action.kind) {
    case "reset":
      return initialState();
    case "expireNudge":
      return { ...state, nudges: state.nudges.filter((n) => n.id !== action.id) };
    case "event":
      return applyEvent(state, action.event);
    default:
      return state;
  }
}

function applyEvent(state: MonitorState, event: CoachingEvent): MonitorState {
  switch (event.type) {
    case "hello":
      // Fresh server snapshot — adopt its rubric (if it sent one).
      return { ...state, rubric: event.rubric.length ? event.rubric : state.rubric };

    case "call_started":
      // New call: clear transcripts/nudges but keep history + the RC debug feed
      // visible across the whole test session.
      return {
        ...initialState(),
        phase: "live",
        counterpart: event.counterpart,
        startedAt: event.startedAt,
        nudgeHistory: state.nudgeHistory,
        rcEvents: state.rcEvents,
        rcStatus: state.rcStatus,
      };

    case "call_ended":
      return { ...state, phase: "ended", endedAt: event.endedAt };

    case "rubric":
      return {
        ...state,
        phase: state.phase === "idle" ? "live" : state.phase,
        rubric: event.rubric.length ? event.rubric : state.rubric,
      };

    case "nudge": {
      const phase: CallPhase = state.phase === "idle" ? "live" : state.phase;
      const liveRest = state.nudges.filter((n) => n.id !== event.nudge.id);
      const histRest = state.nudgeHistory.filter((n) => n.id !== event.nudge.id);
      return {
        ...state,
        phase,
        nudges: [event.nudge, ...liveRest],
        nudgeHistory: [event.nudge, ...histRest],
      };
    }

    case "transcript":
      return applyTranscript(state, event.line);

    case "reference":
      // Not surfaced in this transcript-focused monitor; accept-and-ignore keeps
      // the union exhaustive.
      return state;

    case "rc_event":
      // Raw RingCentral telephony event — prepend to the debug feed (capped).
      return { ...state, rcEvents: [event, ...state.rcEvents].slice(0, RC_FEED_MAX) };

    case "rc_status":
      return { ...state, rcStatus: event };

    default:
      return state;
  }
}

function applyTranscript(state: MonitorState, line: TranscriptLine): MonitorState {
  const phase: CallPhase = state.phase === "idle" ? "live" : state.phase;

  if (line.speaker === "customer") {
    return line.final
      ? { ...state, phase, customerLines: [...state.customerLines, line], customerInterim: null }
      : { ...state, phase, customerInterim: line };
  }
  if (line.speaker === "rep") {
    return line.final
      ? { ...state, phase, repLines: [...state.repLines, line], repInterim: null }
      : { ...state, phase, repInterim: line };
  }
  // 'unknown' — only keep finals, in a muted shared spot; drop interims.
  return line.final ? { ...state, phase, unknownLines: [...state.unknownLines, line] } : state;
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                      */
/* -------------------------------------------------------------------------- */

/** mm:ss from a ms-from-call-start offset. */
function fmtClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function labelForCriterion(key: string): string {
  return RUBRIC.find((c) => c.key === key)?.label ?? key;
}

/** Hook: keep a scroll container pinned to the bottom as content grows. */
function useStickToBottom<T>(dep: T) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [dep]);
  return ref;
}

/* -------------------------------------------------------------------------- */
/* Screen                                                                       */
/* -------------------------------------------------------------------------- */

export function LiveMonitor() {
  const [state, dispatch] = useReducer(reducer, undefined, initialState);
  const [conn, setConn] = useState<ConnStatus>("connecting");

  // ── WebSocket transport with auto-reconnect + backoff ─────────────────────
  useEffect(() => {
    let ws: WebSocket | null = null;
    let stopped = false;
    let attempt = 0;
    let retryTimer: number | undefined;

    const connect = async () => {
      if (stopped) return;
      setConn("connecting");
      // Admin firehose: authenticate with the signed-in admin's Supabase token
      // (the route is already admin-gated; the server re-checks the role too).
      const token = await getAccessToken();
      if (stopped) return;
      if (!token) {
        setConn("closed");
        scheduleRetry();
        return;
      }
      const url = `${WS_BASE.replace(/\/$/, "")}/coach?monitor=1&token=${encodeURIComponent(token)}`;
      try {
        ws = new WebSocket(url);
      } catch {
        scheduleRetry();
        return;
      }

      ws.onopen = () => {
        attempt = 0;
        setConn("open");
      };

      ws.onmessage = (msg) => {
        let parsed: unknown;
        try {
          parsed = JSON.parse(typeof msg.data === "string" ? msg.data : "");
        } catch {
          return; // ignore malformed frames
        }
        if (parsed && typeof parsed === "object" && "type" in parsed) {
          dispatch({ kind: "event", event: parsed as CoachingEvent });
        }
      };

      ws.onerror = () => {
        // Don't surface the raw error; the close handler drives reconnect.
        ws?.close();
      };

      ws.onclose = () => {
        if (stopped) return;
        setConn("closed");
        scheduleRetry();
      };
    };

    const scheduleRetry = () => {
      if (stopped) return;
      const idx = Math.min(attempt, BACKOFF_MS.length - 1);
      const delay = BACKOFF_MS[idx] ?? 15000;
      attempt += 1;
      retryTimer = window.setTimeout(() => void connect(), delay);
    };

    void connect();

    return () => {
      stopped = true;
      if (retryTimer !== undefined) window.clearTimeout(retryTimer);
      if (ws) {
        ws.onopen = null;
        ws.onmessage = null;
        ws.onerror = null;
        ws.onclose = null;
        ws.close();
      }
    };
  }, []);

  // ── Nudge expiry sweep: remove each active nudge after its own ttlMs ───────
  const timersRef = useRef<Map<string, number>>(new Map());
  useEffect(() => {
    const timers = timersRef.current;
    for (const nudge of state.nudges) {
      if (timers.has(nudge.id)) continue;
      const handle = window.setTimeout(() => {
        timers.delete(nudge.id);
        dispatch({ kind: "expireNudge", id: nudge.id });
      }, Math.max(1000, nudge.ttlMs));
      timers.set(nudge.id, handle);
    }
    // Drop timers for nudges no longer active.
    for (const [id, handle] of timers) {
      if (!state.nudges.some((n) => n.id === id)) {
        window.clearTimeout(handle);
        timers.delete(id);
      }
    }
  }, [state.nudges]);

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const handle of timers.values()) window.clearTimeout(handle);
      timers.clear();
    };
  }, []);

  return (
    <div className="flex h-screen flex-col bg-background text-foreground">
      <HeaderBar conn={conn} phase={state.phase} counterpart={state.counterpart} />
      <div className="grid min-h-0 flex-1 grid-cols-1 divide-y md:grid-cols-3 md:divide-x md:divide-y-0">
        <TranscriptColumn
          title="Customer"
          accent="text-sky-600 dark:text-sky-400"
          lines={state.customerLines}
          interim={state.customerInterim}
          unknownLines={state.unknownLines}
        />
        <TranscriptColumn
          title="Rep"
          accent="text-emerald-600 dark:text-emerald-400"
          lines={state.repLines}
          interim={state.repInterim}
        />
        <CoachColumn
          rubric={state.rubric}
          nudges={state.nudges}
          history={state.nudgeHistory}
        />
      </div>
      <RcEventPanel rcStatus={state.rcStatus} rcEvents={state.rcEvents} />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* RingCentral debug visualizer (raw event feed + socket status)                */
/* -------------------------------------------------------------------------- */

function RcStatusBadge({ status }: { status: RcStatus | null }) {
  const state = status?.state ?? "—";
  const color =
    state === "subscribed"
      ? "bg-emerald-500"
      : state === "open"
        ? "bg-amber-500"
        : state === "error" || state === "closed"
          ? "bg-red-500"
          : "bg-slate-400";
  return (
    <span className="flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px]">
      <span className={`h-1.5 w-1.5 rounded-full ${color}`} />
      <span>RC socket: {state}</span>
      {status?.detail ? <span className="text-muted-foreground">· {status.detail}</span> : null}
    </span>
  );
}

function RcEventRow({ ev }: { ev: RcEvent }) {
  const time = new Date(ev.at).toLocaleTimeString();
  const sid = ev.telephonySessionId ? ev.telephonySessionId.slice(-6) : "—";
  return (
    <div
      className={`flex flex-wrap items-baseline gap-x-2 gap-y-0.5 border-b border-border/40 py-0.5 ${
        ev.inScope ? "text-foreground" : "text-muted-foreground"
      }`}
    >
      <span className="tabular-nums text-muted-foreground">{time}</span>
      {ev.inScope ? (
        <span className="rounded bg-emerald-500/15 px-1 text-emerald-600 dark:text-emerald-400">IN-SCOPE</span>
      ) : null}
      <span className="text-muted-foreground">sid …{sid}</span>
      {ev.parties.map((p, i) => (
        <span key={i} className="rounded bg-muted px-1">
          {p.ext ?? "?"}:{p.status ?? "?"}
          {p.number ? ` (${p.number})` : ""}
        </span>
      ))}
    </div>
  );
}

function RcEventPanel({ rcStatus, rcEvents }: { rcStatus: RcStatus | null; rcEvents: RcEvent[] }) {
  return (
    <section className="flex h-48 shrink-0 flex-col border-t bg-muted/20" aria-label="RingCentral event feed">
      <div className="flex shrink-0 items-center justify-between gap-3 border-b px-3 py-1.5">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            RingCentral feed (raw — independent of SIP/supervise)
          </span>
          <RcStatusBadge status={rcStatus} />
        </div>
        <span className="text-[11px] tabular-nums text-muted-foreground">
          {rcEvents.length} event{rcEvents.length === 1 ? "" : "s"}
        </span>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-1.5 font-mono text-[11px] leading-relaxed">
        {rcEvents.length === 0 ? (
          <p className="py-5 text-center text-muted-foreground">
            No telephony events yet. If a call connects on the account and nothing shows here, RingCentral
            isn&apos;t delivering events — a detection problem, not the SIP/audio path.
          </p>
        ) : (
          rcEvents.map((e, i) => <RcEventRow key={`${e.at}-${i}`} ev={e} />)
        )}
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Header                                                                       */
/* -------------------------------------------------------------------------- */

function HeaderBar({
  conn,
  phase,
  counterpart,
}: {
  conn: ConnStatus;
  phase: CallPhase;
  counterpart: string | null;
}) {
  return (
    <header className="flex shrink-0 items-center justify-between gap-3 border-b px-4 py-2.5">
      <div className="flex items-center gap-3">
        <h1 className="text-sm font-semibold tracking-tight">Live Call Monitor (test)</h1>
        <CallStateIndicator phase={phase} counterpart={counterpart} />
      </div>
      <ConnPill conn={conn} />
    </header>
  );
}

function ConnPill({ conn }: { conn: ConnStatus }) {
  const map: Record<ConnStatus, { tone: "muted" | "success" | "danger"; label: string; dot: string }> = {
    connecting: { tone: "muted", label: "Connecting", dot: "bg-amber-400 animate-pulse" },
    open: { tone: "success", label: "Live", dot: "bg-emerald-500" },
    closed: { tone: "danger", label: "Disconnected", dot: "bg-red-500" },
  };
  const { tone, label, dot } = map[conn];
  return (
    <Badge tone={tone}>
      <span className="flex items-center gap-1.5">
        <span aria-hidden className={`inline-block h-2 w-2 rounded-full ${dot}`} />
        {label}
      </span>
    </Badge>
  );
}

function CallStateIndicator({
  phase,
  counterpart,
}: {
  phase: CallPhase;
  counterpart: string | null;
}) {
  if (phase === "live") {
    return (
      <Badge tone="success">
        Call in progress{counterpart ? ` · ${counterpart}` : ""}
      </Badge>
    );
  }
  if (phase === "ended") return <Badge tone="muted">Call ended</Badge>;
  return <Badge tone="muted">Idle</Badge>;
}

/* -------------------------------------------------------------------------- */
/* Transcript columns                                                           */
/* -------------------------------------------------------------------------- */

function TranscriptColumn({
  title,
  accent,
  lines,
  interim,
  unknownLines,
}: {
  title: string;
  accent: string;
  lines: TranscriptLine[];
  interim: TranscriptLine | null;
  unknownLines?: TranscriptLine[];
}) {
  // Re-pin to bottom whenever the visible content changes.
  const scrollRef = useStickToBottom([lines.length, interim?.text, unknownLines?.length]);
  const empty = lines.length === 0 && !interim && (unknownLines?.length ?? 0) === 0;

  return (
    <section className="flex min-h-0 flex-col" aria-label={`${title} transcript`}>
      <ColumnHeader>
        <span className={`text-xs font-bold uppercase tracking-wide ${accent}`}>{title}</span>
        <span className="text-[11px] tabular-nums text-muted-foreground">{lines.length} lines</span>
      </ColumnHeader>
      <div ref={scrollRef} className="min-h-0 flex-1 space-y-1.5 overflow-y-auto px-3 py-2">
        {empty ? (
          <p className="px-1 py-6 text-center text-xs text-muted-foreground">
            Waiting for {title.toLowerCase()} speech…
          </p>
        ) : (
          <>
            {lines.map((line, i) => (
              <TranscriptRow key={`f-${i}-${line.startMs}`} line={line} />
            ))}
            {unknownLines?.map((line, i) => (
              <TranscriptRow key={`u-${i}-${line.startMs}`} line={line} muted />
            ))}
            {interim && <TranscriptRow line={interim} interim />}
          </>
        )}
      </div>
    </section>
  );
}

function TranscriptRow({
  line,
  interim = false,
  muted = false,
}: {
  line: TranscriptLine;
  interim?: boolean;
  muted?: boolean;
}) {
  return (
    <div className="flex gap-2 leading-snug">
      <span className="shrink-0 select-none pt-0.5 font-mono text-[10px] tabular-nums text-muted-foreground">
        {fmtClock(line.startMs)}
      </span>
      <p
        className={[
          "text-sm",
          interim ? "italic text-muted-foreground/80" : "",
          muted && !interim ? "text-muted-foreground" : "",
        ].join(" ")}
      >
        {line.text}
        {interim && <span aria-hidden className="ml-1 animate-pulse">▋</span>}
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* AI Coach column                                                              */
/* -------------------------------------------------------------------------- */

function CoachColumn({
  rubric,
  nudges,
  history,
}: {
  rubric: RubricState;
  nudges: Nudge[];
  history: Nudge[];
}) {
  // History entries that aren't currently active (so we don't double-show).
  const activeIds = new Set(nudges.map((n) => n.id));
  const pastNudges = history.filter((n) => !activeIds.has(n.id));
  const scrollRef = useStickToBottom([nudges.length, history.length]);

  return (
    <section className="flex min-h-0 flex-col" aria-label="AI coach">
      <ColumnHeader>
        <span className="text-xs font-bold uppercase tracking-wide text-violet-600 dark:text-violet-400">
          AI Coach
        </span>
      </ColumnHeader>
      <div ref={scrollRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto px-3 py-2">
        <RubricChecklist rubric={rubric} />

        <div className="space-y-1.5">
          <SubLabel>Nudges</SubLabel>
          {nudges.length === 0 ? (
            <p className="rounded-md border border-dashed px-2 py-3 text-center text-xs text-muted-foreground">
              Recommendations appear here in real time.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {nudges.map((n) => (
                <NudgeCard key={n.id} nudge={n} />
              ))}
            </ul>
          )}
        </div>

        {pastNudges.length > 0 && (
          <div className="space-y-1.5">
            <SubLabel>History</SubLabel>
            <ul className="space-y-1">
              {pastNudges.map((n) => (
                <li
                  key={`h-${n.id}`}
                  className="flex items-start gap-2 rounded-md px-2 py-1 text-xs text-muted-foreground"
                >
                  <span className="shrink-0 pt-0.5 font-mono text-[10px] tabular-nums">
                    {fmtClock(n.atMs)}
                  </span>
                  <span className="line-clamp-2">{n.text}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}

const SEVERITY_STYLE: Record<Nudge["severity"], string> = {
  info: "border-l-4 border-l-slate-400 bg-slate-50 dark:bg-slate-900/40",
  prompt: "border-l-4 border-l-amber-400 bg-amber-50 dark:bg-amber-950/30",
  urgent: "border-l-4 border-l-red-500 bg-red-50 dark:bg-red-950/30",
};

const SEVERITY_TONE: Record<Nudge["severity"], "muted" | "warning" | "danger"> = {
  info: "muted",
  prompt: "warning",
  urgent: "danger",
};

function NudgeCard({ nudge }: { nudge: Nudge }) {
  return (
    <li className={`rounded-md border p-2.5 shadow-sm ${SEVERITY_STYLE[nudge.severity]}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium leading-snug">{nudge.text}</p>
        <Badge tone={SEVERITY_TONE[nudge.severity]}>{nudge.severity}</Badge>
      </div>
      <div className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
        <span className="font-mono tabular-nums">{fmtClock(nudge.atMs)}</span>
        {nudge.criterionKey && (
          <>
            <span aria-hidden>·</span>
            <span>{labelForCriterion(nudge.criterionKey)}</span>
          </>
        )}
      </div>
    </li>
  );
}

function RubricChecklist({ rubric }: { rubric: RubricState }) {
  const total = rubric.reduce((sum, c) => sum + (c.covered ? c.weight : 0), 0);
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <SubLabel>Checklist</SubLabel>
        <span className="text-sm font-semibold tabular-nums">
          {total}
          <span className="text-muted-foreground">/100</span>
        </span>
      </div>
      <ul className="space-y-1 rounded-lg border p-1.5">
        {rubric.map((c) => (
          <RubricRow key={c.key} criterion={c} />
        ))}
      </ul>
    </div>
  );
}

function RubricRow({ criterion }: { criterion: CriterionState }) {
  const { covered, label, weight } = criterion;
  return (
    <li
      className={[
        "flex items-center gap-2.5 rounded-md px-2 py-1.5 transition",
        covered
          ? "bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100"
          : "text-foreground",
      ].join(" ")}
    >
      <span
        aria-hidden
        className={[
          "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[11px] font-bold",
          covered
            ? "border-emerald-500 bg-emerald-500 text-white"
            : "border-muted-foreground/40 text-transparent",
        ].join(" ")}
      >
        ✓
      </span>
      <span className={`flex-1 text-sm leading-tight ${covered ? "font-medium" : ""}`}>{label}</span>
      <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">{weight} pts</span>
    </li>
  );
}

/* -------------------------------------------------------------------------- */
/* Shared bits                                                                  */
/* -------------------------------------------------------------------------- */

function ColumnHeader({ children }: { children: ReactNode }) {
  return (
    <div className="flex shrink-0 items-center justify-between border-b bg-muted/40 px-3 py-1.5">
      {children}
    </div>
  );
}

function SubLabel({ children }: { children: ReactNode }) {
  return (
    <h2 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </h2>
  );
}
